import { defineStore } from 'pinia'
import { bridgeService } from '@/services/bridge'
import { errorMessage } from '@/services/http'
import { triggerDownload } from '@/utils/download'
import { createLatest } from '@/utils/latest'
import type { BridgeStatus, PairCode } from '@/types/api'

const latest = createLatest()

interface BridgeState {
  online: boolean
  deviceName: string | null
  downloadable: boolean
  pairCode: PairCode | null
  loading: boolean
  ready: boolean
  error: string | null
}

export const useBridgeStore = defineStore('bridge', {
  state: (): BridgeState => ({
    online: false,
    deviceName: null,
    downloadable: false,
    pairCode: null,
    loading: false,
    ready: false,
    error: null,
  }),

  getters: {
    paired: (state): boolean => state.deviceName !== null,
  },

  actions: {
    async fetchStatus(): Promise<void> {
      if (this.loading) return
      const isCurrent = latest.claim()
      this.loading = true
      this.error = null
      try {
        const status = await bridgeService.status()
        if (!isCurrent()) return
        this.apply(status)
        this.downloadable = status.download
      } catch (e) {
        if (isCurrent()) this.error = errorMessage(e)
      } finally {
        this.loading = false
        this.ready = true
      }
    },

    async fetchOnce(): Promise<void> {
      if (this.ready || this.loading) return
      await this.fetchStatus()
    },

    apply(status: BridgeStatus): void {
      latest.invalidate()
      this.online = status.online
      this.deviceName = status.device_name
      if (status.online) this.pairCode = null
    },

    async requestPairCode(): Promise<void> {
      this.loading = true
      this.error = null
      try {
        this.pairCode = await bridgeService.pairCode()
      } catch (e) {
        this.error = errorMessage(e)
      } finally {
        this.loading = false
      }
    },

    download(): void {
      this.error = null
      triggerDownload(bridgeService.downloadUrl())
    },

    async unpair(): Promise<void> {
      this.loading = true
      this.error = null
      try {
        await bridgeService.unpair()
        latest.invalidate()
        this.online = false
        this.deviceName = null
        this.pairCode = null
      } catch (e) {
        this.error = errorMessage(e)
      } finally {
        this.loading = false
      }
    },
  },
})
